import React from 'react';

import { Empty } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';

import { useNw3Payment } from '@hooks/API/UsePayments';

import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import NwResult from '@components/Gui/NwResult';

const LEGACY_DEFAULT_CURRENCY = 'EUR';

const Content = styled.div`
    background: #fff;
    padding: 2rem 2.5rem;
    color: #000;

    .legacy-note-header {
        margin-bottom: 2rem;

        .legacy-note-title {
            font-size: 1.5rem;
            line-height: 1.1;
            font-weight: 600;
            text-transform: uppercase;
        }

        .legacy-note-subtitle {
            margin-top: 0.25rem;
            font-size: 1.25rem;
            line-height: 1.2;
        }

        .legacy-note-text {
            margin-top: 0.75rem;
            white-space: pre-wrap;
        }
    }

    .legacy-payment-table {
        width: 100%;
        border: 1px solid #222;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 1rem;

        th {
            border-bottom: 1px solid #222;
            font-weight: 400;
            text-align: center;
            padding: 0.25rem 0.4rem;
        }

        th.amount-column,
        td.amount-column {
            width: 13rem;
            border-left: 1px solid #222;
        }

        tbody td {
            vertical-align: top;
            padding: 0.25rem 0.4rem 0.7rem;
        }

        tbody tr:first-child td {
            padding-top: 0.35rem;
        }

        tbody tr:last-child td {
            padding-bottom: 5rem;
        }

        tfoot td {
            border-top: 1px solid #222;
            vertical-align: top;
            padding: 0.35rem 0.4rem;
        }

        .legacy-service-main {
            font-weight: 600;
            line-height: 1.2;
        }

        .legacy-service-detail {
            line-height: 1.2;
        }

        .legacy-service-work-date {
            line-height: 1.2;
        }

        .legacy-amount-cell {
            text-align: right;
            white-space: nowrap;
            line-height: 1.2;
        }

        .legacy-total-label {
            line-height: 1.2;
        }

        .legacy-total-lines {
            text-align: right;
            line-height: 1.2;
            white-space: nowrap;
        }
    }

    @media (max-width: 720px) {
        padding: 1rem;

        .legacy-payment-table {
            font-size: 0.9rem;

            th.amount-column,
            td.amount-column {
                width: 8rem;
            }
        }
    }
`;

const isEmptyValue = (value) => value === null || value === undefined || value === '';

const getNumericValue = (value) => {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? 0 : numericValue;
    }

    return 0;
};

const formatDate = (date) => {
    if (!date) {
        return null;
    }

    const parsedDate = getMoment(date);
    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : null;
};

const formatAmount = (amount, currency) => {
    return `${new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(getNumericValue(amount))} ${currency || LEGACY_DEFAULT_CURRENCY}`;
};

const getPaymentNumber = (paymentData) => {
    return paymentData?.ProgGen || paymentData?.Code || paymentData?.ID || '-';
};

const getPaymentCurrency = (paymentData) => {
    const firstRow = paymentData?.Rows?.[0];
    const firstStatement = firstRow?.Statement;

    return paymentData?.Currency || firstRow?.Currency || firstStatement?.Currency || LEGACY_DEFAULT_CURRENCY;
};

const getPaymentRows = (paymentData) => {
    return Array.isArray(paymentData?.Rows) ? paymentData.Rows : [];
};

const getRowStatement = (row) => {
    return row?.Statement || {};
};

const getRowTransaction = (row) => {
    return getRowStatement(row).Transaction || {};
};

const getRowProject = (row) => {
    return getRowTransaction(row).Project || {};
};

const getMainServiceLine = (row) => {
    const project = getRowProject(row);
    const projectName = project.Name;
    const productName = project.Product;

    if (projectName && productName) {
        return `${projectName} - ${productName}`;
    }

    return projectName || productName || getRowStatement(row).Description || `Statement ${row.StatementID || row.ID}`;
};

const getDetailServiceLine = (row) => {
    const statement = getRowStatement(row);
    const project = getRowProject(row);
    const detailText = statement.Description || project.Description;

    if (!detailText || detailText === project.Product) {
        return null;
    }

    return detailText;
};

const getRowWorkDate = (row) => {
    const statement = getRowStatement(row);
    const transaction = getRowTransaction(row);
    const project = getRowProject(row);

    return statement.RefDate || transaction.WorkDate || project.FirstDate;
};

const getTotals = (rows) => {
    return rows.reduce(
        (totals, row) => ({
            amount: totals.amount + getNumericValue(row.Amount),
            taxes: totals.taxes + getNumericValue(row.TaxAmount),
        }),
        { amount: 0, taxes: 0 }
    );
};

const LegacyPaymentRowsTable = ({ paymentData }) => {
    const rows = getPaymentRows(paymentData);

    if (!rows.length) {
        return <Empty description="No paid items found in legacy payment data" />;
    }

    const currency = getPaymentCurrency(paymentData);
    const totals = getTotals(rows);
    const netAmount = totals.amount - totals.taxes;

    return (
        <table className="legacy-payment-table">
            <thead>
                <tr>
                    <th>description of service</th>
                    <th className="amount-column">amount</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => {
                    const workDate = formatDate(getRowWorkDate(row));
                    const detailLine = getDetailServiceLine(row);

                    return (
                        <tr key={row.ID || row.StatementID}>
                            <td>
                                <div className="legacy-service-main">{getMainServiceLine(row)}</div>
                                {detailLine && <div className="legacy-service-detail">{detailLine}</div>}
                                {workDate && <div className="legacy-service-work-date">work date:{workDate}</div>}
                            </td>
                            <td className="amount-column legacy-amount-cell">
                                {formatAmount(row.Amount, currency)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr>
                    <td className="legacy-total-label">total note</td>
                    <td className="amount-column legacy-total-lines">
                        <div>{formatAmount(totals.amount, currency)}</div>
                        <div>taxes: - {formatAmount(totals.taxes, currency)}</div>
                        <div>= {formatAmount(netAmount, currency)}</div>
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

const LegacyPaymentContent = ({ nw3PaymentId }) => {
    const { data: paymentData, isFetching, error } = useNw3Payment(nw3PaymentId);

    if (isFetching) {
        return <Loading textBlack small />;
    }

    if (error) {
        return <NwResult status="error" title="Legacy payment not available" />;
    }

    const paymentDate = formatDate(paymentData?.DateDoc);

    return (
        <Content>
            <div className="legacy-note-header">
                <div className="legacy-note-title">NOTE</div>
                <div className="legacy-note-subtitle">
                    {getPaymentNumber(paymentData)}
                    {paymentDate ? ` on ${paymentDate}` : ''}
                </div>
                {!isEmptyValue(paymentData?.Note) && <div className="legacy-note-text">{paymentData.Note}</div>}
            </div>

            <LegacyPaymentRowsTable paymentData={paymentData} />
        </Content>
    );
};

const PaymentsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

// Accepts either a single ID (nw3PaymentId) or a list of IDs (nw3PaymentIds) and stacks a note per payment
const LegacyPaymentDrawer = ({ nw3PaymentId, nw3PaymentIds, onClose }) => {
    const paymentIds = nw3PaymentIds?.length ? nw3PaymentIds : nw3PaymentId ? [nw3PaymentId] : [];

    return (
        <NwDrawer
            width="58rem"
            maxWidth="94vw"
            padding="1rem"
            title="Legacy Payment Details"
            onClose={onClose}
            whiteBg={false}
        >
            <PaymentsList>
                {paymentIds.map((paymentId) => (
                    <LegacyPaymentContent key={paymentId} nw3PaymentId={paymentId} />
                ))}
            </PaymentsList>
        </NwDrawer>
    );
};

export default LegacyPaymentDrawer;
